import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled';

import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity';
import { SecondaryNavigation } from 'components/ui/secondary-navigation';
import { SectionHeader } from 'components/ui/section-header';
import { StatusGuard } from 'components/ui/status-guard';
import { Modal } from 'components/ui/modal';
import { connect } from 'containers/store';
import { Space } from 'generated/mos/structure';
import { spacesBindableActionCreators } from 'domains/spaces/actions';
import { DataStatus } from 'domains/spaces/store';
import { structuresActionCreators } from 'domains/structures/actions';
import { DataStatus as StructuresDataStatus } from 'domains/structures/store';
import { Structures } from 'domains/structures/entities';
import { GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';
import { pick } from 'helpers/core';
import { statusSelector } from 'helpers/status';
import { LayoutListOrFormPage } from 'layouts/list-or-form-page';
import { getAnalyticsSubnav } from '../subnav';
import { QueryForm } from './query-form';
import { AuthSession } from 'domains/account/auth';
import { QueryProps, useQuery } from './use-query';
import { DensityMap, getConfigurator } from './density-map';
import {configData} from './config-data';
import { formatSiteFeatures, generateSiteLevelsLookup, SiteFeatures } from './map-data-utils';
import { space } from 'helpers/style';
import { StatusBar } from './status-bar';
import { Loader } from 'components/ui/loader';
import { QUERY_STATUS } from './queryStatus';

type ConnectedProps = {
  analytics: GettyStore['getty']['analytics'];
  readonly spacesList: DataStatus<ReadonlyArray<Space.Entity>>;
  readonly structures: StructuresDataStatus<Structures>;
  readonly session: AuthSession | undefined;
};


type ActionProps = Pick<typeof spacesBindableActionCreators, 'spacesRequest'> &
  Pick<typeof structuresActionCreators, 'structuresLoadRequest'>;

type DirectProps = {};
type Props = ConnectedProps & ActionProps & DirectProps;

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 70vh;
  
  position: relative;
  overflow: hidden;
  width: 100%;
  background-color: ${({ theme }) => theme.color.white};
  .maps {
    padding: ${space(2)};
    background-color: ${({ theme }) => theme.color.white};
    gap: ${space(1)};
    display: flex;
    align-items: flex-start;
    justify-content: flex-start
    > * {
      width: 50%;
    }
  }
`;

enum LEVEL_STYLE {
  SPLIT = 'split',
  STACKED = 'stacked'
}

export const AnalyticsVisitorTrafficView = (props: Props) => {
  
  const {sendQuery, queryStatus} = useQuery();
  const [levelDisplayStyle, setLevelDisplayStyle] = useState(LEVEL_STYLE.STACKED);
  const [dateModalIsOpen, setDateModalIsOpen] = useState(true);
  const [token, setToken] = useState('');
  const config = getConfigurator(configData);
  const spaceData = statusSelector.data(props.spacesList);
  const structureData = statusSelector.data(props.structures);
  
  const closeModal = () => setDateModalIsOpen(false);
  const openModal = () => setDateModalIsOpen(true);
  const toggleLevelDisplayStyle = () => setLevelDisplayStyle(
    levelDisplayStyle === LEVEL_STYLE.SPLIT 
      ? LEVEL_STYLE.STACKED
      : LEVEL_STYLE.SPLIT
    );
  
  const updateFeatureCollections = () => {
    if ( !structureData || !spaceData) return;
    const stackLevels = levelDisplayStyle === LEVEL_STYLE.STACKED;
    const siteLevelsLookup = generateSiteLevelsLookup(structureData, spaceData, config.excludeLevels)
    return Object.keys(siteLevelsLookup).reduce((acc: SiteFeatures[], siteId) => {
      const levelData = formatSiteFeatures(siteLevelsLookup, siteId, config, stackLevels);
      return [...acc, levelData];
    },[]);
  }
  
  useEffect(() => {
    if (!props.session || !props.session.access) return;
    setToken(props.session.access);
  }, [props.session]);

  useEffect(() => {
    statusSelector.shouldLoad(props.spacesList.status) && props.spacesRequest();
  });

  useEffect(() => {
    statusSelector.shouldLoad(props.structures.status) && props.structuresLoadRequest();
  });
  
  const prepareQuery = (query: QueryProps) => {
    setDateModalIsOpen(false);
    sendQuery(token, query);
  }
  
  const getLevelMaps = () => {
    
    if (queryStatus.type === QUERY_STATUS.LOADING) return <Loader />;
    if (queryStatus.type !== QUERY_STATUS.SUCCESS) return null;
    
    const result = queryStatus.result;
    const siteRef = queryStatus.queryProps.siteRef;
    
    const features = updateFeatureCollections();
    const siteFeatures = features && siteRef && features.find(collection => collection.siteId === siteRef);

    // ugly stuff here for dealing with potentially undefined values;
    if (!siteFeatures) return null;

    return levelDisplayStyle === LEVEL_STYLE.SPLIT 
      // split levels -- display each level in its own map
      ? siteFeatures.levelNumbers.map(level => (
        <DensityMap
          densityData={result}
          level={level}
          site={siteFeatures}
          config={config}
          key={`level_${level}_density_map`}
        />
      ))
      // stacked levels -- display all levels on a single map
      : (
        <DensityMap      
          densityData={result}
          site={siteFeatures}
          config={config}
          key="density_map"
          playbackControl
          fullscreenControl
        />
      ) 
  }

  const modal = (
    <Modal onClose={closeModal} header="New Query">
      <QueryForm sendQuery={prepareQuery} />
    </Modal>
  )

  const statusBar = (
    <StatusBar
      onStyleToggle={toggleLevelDisplayStyle}
      onNewQuery={openModal}
      status={queryStatus}
    />
  );

  return (
    <StatusGuard status={props.spacesList}>
      <StatusGuard status={props.structures}>
        <LayoutListOrFormPage
          nav={() => <PrimaryNavigationEditEntity title="Heat Map" navBackRoute="/" darkTheme />}
          title="Heat Map"
          subnav={() => <SecondaryNavigation navItems={getAnalyticsSubnav()} />}
          content={() => (
            <>
              <SectionHeader heading="Getty Guide" />
              <Container>
                { dateModalIsOpen ? modal : statusBar }
                <div className="maps">{ getLevelMaps() }</div>
              </Container>
            </>
          )}
        />
      </StatusGuard>
    </StatusGuard>
  );
};

export const AnalyticsVisitorTraffic = connect<
  ConnectedProps,
  ActionProps,
  DirectProps,
  GettyStore,
  GettyActionCreators
>(
  store => ({
    analytics: store.getty.analytics,
    ...pick(store.spaces, 'spacesList'),
    ...pick(store.structures, 'structures'),
    ...pick(store.account, 'session')
  }),
  {
    ...pick(spacesBindableActionCreators, 'spacesRequest'),
    ...pick(structuresActionCreators, 'structuresLoadRequest')
  }
)(AnalyticsVisitorTrafficView);
